// Shared helpers for turning a race prediction API result into the
// `workoutData` shape RunningAnalysisView (and its map/chart/grid children) expect.

/**
 * Convert prediction response to the workoutData shape RunningAnalysisView expects
 * @param {Object} result - Prediction API result (predicted_chunks, summary, checkpoints, climbs, descents)
 * @param {Object|null} race - Race catalog record
 * @param {string} startTimeStr - "HH:MM" in UTC — used to derive workout_start_time so timing columns work
 * @returns {Object} workoutData
 */
export function buildPredictionWorkoutData(result, race = null, startTimeStr = '07:00') {
  const { predicted_chunks, summary, checkpoints, climbs, descents } = result;

  // Build a synthetic ISO datetime for the race start (fixed date 2000-01-01, UTC)
  // so that formatArrivalTime / parseCutoffMs work correctly in RunningCheckpointsGrid
  const [hh, mm] = (startTimeStr || '07:00').split(':');
  const raceStartIso = `2000-01-01T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00+00:00`;

  // Add a minimal points array per chunk so the map can draw the route
  const chunksWithPoints = predicted_chunks.map((c) => ({
    ...c,
    points: c.lat != null && c.lon != null
      ? [{ lat: c.lat, lon: c.lon, cumulative_distance: c.distance_start_m, is_moving: !c.is_paused }]
      : [],
  }));

  // Remap prediction checkpoint fields to what RunningCheckpointsGrid expects
  const mappedCheckpoints = (checkpoints || []).map((cp, i) => ({
    id: i,
    name: cp.checkpoint_name,
    // Official catalog distance — shown in "Official km" column when race prop is provided
    distance_km: cp.distance_km ?? (cp.distance_m != null ? +(cp.distance_m / 1000).toFixed(2) : null),
    // Predicted distance — presence of this field triggers the analyzed-data columns
    fit_distance_km: cp.distance_m != null ? +(cp.distance_m / 1000).toFixed(2) : null,
    arrival_elapsed_seconds: cp.predicted_elapsed_time_s ?? null,
    stopping_time_seconds: Math.round(cp.predicted_stop_time_s || 0),
    cutoff_time: cp.cutoff_time || null,
    fastest_time: cp.fastest_time || null,
    slowest_time: cp.slowest_time || null,
    services: cp.services || null,
    assistance_permitted: cp.assistance_permitted ?? null,
    warm_meal: cp.warm_meal ?? null,
    spare_bag: cp.spare_bag ?? null,
    rest_area: cp.rest_area ?? null,
    type: 'checkpoint',
    status: cp.status || 'detected',
    // Matched lat/lon for map markers (fit_lat/fit_lon used by RunningRouteMap)
    fit_lat: cp.lat ?? null,
    fit_lon: cp.lon ?? null,
    lat: cp.lat ?? null,
    lon: cp.lon ?? null,
  }));

  // Moving norm pace (GAP): distance-weighted mean over moving chunks — consistent with real workouts.
  // RunningAnalysisView recomputes avg_gap (Avg Norm. Pace) as moving_avg_gap × (total_time /
  // moving_time) using the same algorithm for real and predicted workouts. avg_gap here is only a
  // fallback for consumers that bypass RunningAnalysisView (e.g. ComparisonSummary).
  const movingChks = predicted_chunks.filter(c => !c.is_paused && (c.gap || 0) > 0 && (c.distance || 0) > 0);
  const gapDistTotal = movingChks.reduce((s, c) => s + c.distance, 0);
  const avgGap = gapDistTotal > 0
    ? movingChks.reduce((s, c) => s + c.gap * c.distance, 0) / gapDistTotal
    : (summary.predicted_avg_pace_min_km || 0);

  // avg_pace uses total time (moving + stops); avg_moving_pace uses moving time only
  const totalDistKm = summary.race_distance_km || 0;
  const avgPace = totalDistKm > 0
    ? (summary.predicted_total_time_s / 60.0) / totalDistKm
    : (summary.predicted_avg_pace_min_km || 0);

  return {
    chunks: chunksWithPoints,
    is_predicted: true,
    workout_start_time: raceStartIso,
    total_distance: totalDistKm * 1000,
    total_duration: summary.predicted_total_time_s || 0,
    total_moving_time: summary.predicted_total_moving_time_s || 0,
    avg_pace: avgPace,
    avg_moving_pace: summary.predicted_avg_pace_min_km || 0,
    best_pace: 0,
    avg_gap: avgGap,
    avg_heart_rate: null,
    stamina_score: summary.stamina_score ?? null,
    stamina_index: summary.stamina_index ?? null,
    total_elevation_gain: summary.total_elevation_gain_m || 0,
    total_elevation_loss: summary.total_elevation_loss_m || 0,
    total_paused_time:
      (summary.predicted_total_checkpoint_stop_time_s || 0) +
      (summary.predicted_total_free_stop_time_s || 0),
    num_paused_chunks: predicted_chunks.filter((c) => c.is_paused).length,
    // Predicted stop split — shown in the Moving Time box (checkpoints vs scattered).
    checkpoint_stop_time_s: summary.predicted_total_checkpoint_stop_time_s ?? null,
    free_stop_time_s: summary.predicted_total_free_stop_time_s ?? null,
    analyzed_checkpoints: mappedCheckpoints.length > 0 ? mappedCheckpoints : undefined,
    climbs: climbs && climbs.length > 0 ? climbs : undefined,
    descents: descents && descents.length > 0 ? descents : undefined,
    race: race || undefined,
    prediction_session_id: result.prediction_session_id || null,
  };
}
